// @givmo/board — "Import older records": one document at a time (CBG-UX11, D-CBG-39; design-284
// `scratchpad/2026-10-24-import-without-batches-design/` §2.1, §2.2, §2.5, §3, frames i1–i4).
//
// Every adopter arrives with a prior corpus — minutes, resolutions, consents, policies, bylaws
// versions — and this is where it lands. Plan §1.3 treats it as first-class, and so does this page:
// a permanent part of "The record", not a one-time onboarding wizard.
//
// ══ WHAT THE PAGE IS ════════════════════════════════════════════════════════════════════════════
//
// It opens straight onto one document: "Record something from before" (`import-one-form.tsx`), with
// nothing to start, name or close first (Jonathan, 2026-10-24 11:48 PDT: "the whole "Batch" concept
// is dark - you can just upload one document at a time for now"). Beside it, "Imported so far": the
// organization's imported records, newest entry first, eight at most — the record list's own read
// filtered `state=imported` — each saying how it came in, by hand or through a connected AI (the
// record's own read, design §4 B5). A first visit lists nothing, so no list is drawn (i1). For a
// whole folder, the page points at the organization's own AI through the connector, in Jonathan's
// sentence (i1; APPROVED).
//
// CBG-AI5-PROPOSE (frame i4; backend `board-ai-proposals.md` §6.2): records the organization's
// connected AI proposed wait at the top, in their own card, until a person confirms or discards them
// on their page.
//
// ══ THE BATCHES, DARK ═══════════════════════════════════════════════════════════════════════════
//
// RC8's import sessions are kept behind the backend's `BOARD_IMPORT_BATCHES_ENABLED`, off everywhere
// (design §3). While it is off their list answers the absent-route 404, read here as "not served"
// (`lib/not-served.ts`), so the page names no batch. Turned on, the batches serve again as built and
// the page lists them below what's imported; the one-document form stays. There is no web flag: the
// backend's answer is the gate.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '../../board-routing';
import {
  isGivmoError,
  type OrgBoardImportBatchOut,
  type OrgBoardRecordReadOut,
} from '@givmo/api-client';
import { extractList } from '@givmo/api-client/list';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Dialog,
  ErrorMessage,
  TextField,
  Textarea,
  toast,
} from '@givmo/design-system';
import { PageShell } from '../../components/page-shell';
import { FieldNote } from '../../components/field-note';
import { useBoardHost } from '../../board-host';
import { hasBoardPermission } from '../../board-host';
import { BOARD_PAGES } from '../../board-routes';
import { displayDate, displayInstant } from '../../lib/display';
import { isNotServed } from '../../lib/not-served';
import {
  batchStatusLabel,
  batchStatusTone,
  extractImportBatches,
  recordCount,
  sortBatches,
} from './import-decode';
import { useBoardOperations } from '../../board-runtime';
import { recordKindLabel } from '../records/records-decode';
// CBG-AI5-PROPOSE: the waiting batches (none for a reader who can't import them).
import { BatchesWaiting } from '../proposals/waiting-lines';
import { useWaitingBatches } from '../proposals/proposal-queries';
import { ImportOneForm } from './import-one-form';
import { importWords as iw, type CameIn } from './import-words';
import { useImportInbox } from './upload-queries';
import { uploadWords as uw } from './upload-words';

/** How many imported records the page lists: the newest entries, the rest in the minute book. */
const IMPORTED_SO_FAR = 8;

const LINE = 'font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary';
const QUIET = 'font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary';
const LINK =
  'font-sans font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline';

export function importBatchesQueryKey(orgRef: string | null): readonly unknown[] {
  return ['board-import-batches', orgRef];
}

/** The import page's list, under the records' key root, so every record write refreshes it. */
function importedSoFarQueryKey(orgRef: string | null): readonly unknown[] {
  return ['board-records', orgRef, 'imported-so-far'];
}

/** What the last "Record it" did: a record went in (with its file or not), or the book already
 *  held it. */
type Outcome =
  | { kind: 'recorded'; title: string; recordId: string; withFile: boolean }
  | { kind: 'already' };

/** How an imported record came in (B5), and who put it in the book: the record's `entered_by`, the
 *  person who entered it by hand or confirmed what an AI proposed (CBG-UX-COPY3's I10). */
function cameIn(record: OrgBoardRecordReadOut): CameIn {
  const by = (record.entered_by_display_name ?? '').trim() || null;
  return record.import_origin === 'connected_ai'
    ? { kind: 'proposed', by }
    : { kind: 'by_hand', by };
}

/** Never retry the route miss: it is an answer, not a blip. */
function retryUnlessNotServed(failureCount: number, error: unknown): boolean {
  return !isNotServed(error) && failureCount < 1;
}

export function ImportBatchesPage() {
  const operations = useBoardOperations();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { permissions, orgRef, isReady } = useBoardHost();
  const canImport = hasBoardPermission(permissions, 'board_record:import');
  const [opening, setOpening] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const waitingBatches = useWaitingBatches();
  // CBG-AI5-INBOX-WEB: `null` while the uploads aren't served here, their read once they are.
  const uploads = useImportInbox();
  const uploadsServed = uploads.allowed && uploads.read.data != null;

  const importedQuery = useQuery({
    queryKey: importedSoFarQueryKey(orgRef),
    enabled: isReady && orgRef !== null,
    queryFn: async (): Promise<OrgBoardRecordReadOut[]> => {
      const { data } = await operations.officerListBoardRecords({
        path: { org_ref: orgRef ?? '' },
        query: { state: 'imported', 'page[limit]': IMPORTED_SO_FAR },
        throwOnError: true,
      });
      return extractList<OrgBoardRecordReadOut>(data, 'data');
    },
  });

  // `null` while the batches are not served here (their route miss), the list once they are.
  const batchesQuery = useQuery({
    queryKey: importBatchesQueryKey(orgRef),
    enabled: isReady && orgRef !== null,
    retry: retryUnlessNotServed,
    queryFn: async (): Promise<OrgBoardImportBatchOut[] | null> => {
      try {
        const { data } = await operations.officerListBoardImportBatches({
          path: { org_ref: orgRef ?? '' },
          throwOnError: true,
        });
        return sortBatches(extractImportBatches(data));
      } catch (error) {
        if (isNotServed(error)) return null;
        throw error;
      }
    },
  });

  const imported = importedQuery.data ?? [];
  const showList = imported.length > 0 || importedQuery.isError;
  const batches = batchesQuery.data ?? null;

  const recorded = (record: OrgBoardRecordReadOut) => {
    setOutcome(
      record.already_recorded === true
        ? { kind: 'already' }
        : {
            kind: 'recorded',
            title: record.title,
            recordId: record.id,
            withFile: Boolean(record.imported_artifact_file_name),
          },
    );
    void queryClient.invalidateQueries({ queryKey: ['board-records', orgRef] });
  };

  return (
    <PageShell
      title="Import older records"
      description={
        // How to import is said only to someone who can (`board_record:import`).
        canImport ? (
          <>
            {iw.pageLead}
            <span className="mt-6 block text-text-tertiary" data-testid="import-connector-line">
              {/* CBG-AI5-INBOX-WEB (frame u1): while the uploads serve, the line carries the upload
                  step and links the upload page; dark, it stays the line it was. */}
              {uploadsServed ? (
                <>
                  {uw.importLineBefore}
                  <Link
                    to={BOARD_PAGES.importUploads}
                    className={LINK}
                    data-testid="import-connector-uploads-link"
                  >
                    {uw.importLineLink}
                  </Link>
                  {uw.importLineAfter}
                </>
              ) : (
                iw.connectorLine
              )}
            </span>
          </>
        ) : (
          iw.pageLeadReader
        )
      }
      testId="page-import-batches"
    >
      {/* CBG-AI5-PROPOSE (frame i4): what waits, above everything else. */}
      <BatchesWaiting batches={waitingBatches} />

      <div
        className={
          showList
            ? 'grid grid-cols-1 items-start gap-24 lg:grid-cols-[minmax(0,1fr)_380px]'
            : 'flex max-w-[680px] flex-col gap-24'
        }
        data-testid="import-page-body"
      >
        {canImport && orgRef !== null ? (
          <div className="flex min-w-0 flex-col gap-16">
            {outcome !== null ? <OutcomeLine outcome={outcome} /> : null}
            <ImportOneForm
              orgRef={orgRef}
              onRecorded={recorded}
              onAlreadyInTheBook={() => setOutcome({ kind: 'already' })}
            />
          </div>
        ) : null}
        {showList ? (
          <ImportedSoFar
            records={imported}
            failed={importedQuery.isError}
            onRetry={() => void importedQuery.refetch()}
          />
        ) : null}
      </div>

      {/* Served only while `BOARD_IMPORT_BATCHES_ENABLED` is on: RC8's batches, as built. */}
      {batches !== null && (batches.length > 0 || canImport) ? (
        <section className="flex flex-col gap-12" data-testid="import-batches-served">
          {canImport ? (
            <div>
              <Button
                appearance="saas"
                tone="secondary"
                disabled={!isReady || orgRef === null}
                onClick={() => setOpening(true)}
                testId="import-batches-open"
              >
                Start a batch
              </Button>
            </div>
          ) : null}
          {batches.map((batch) => (
            <BatchCard key={batch.id} batch={batch} />
          ))}
        </section>
      ) : null}

      {batchesQuery.isError ? (
        <Card testId="import-batches-error">
          <CardBody className="flex flex-col items-start gap-12">
            <p className="font-sans text-s14 font-w400 tracking-givmo text-text-secondary">
              We couldn’t load this board’s batches.
            </p>
            <Button
              appearance="saas"
              tone="secondary"
              onClick={() => void batchesQuery.refetch()}
              testId="import-batches-retry"
            >
              Try again
            </Button>
          </CardBody>
        </Card>
      ) : null}

      {opening && orgRef !== null ? (
        <OpenBatchDialog
          orgRef={orgRef}
          onClose={() => setOpening(false)}
          onOpened={(batch) => {
            setOpening(false);
            void queryClient.invalidateQueries({
              queryKey: importBatchesQueryKey(orgRef),
            });
            void navigate({ to: '/import-batches/$batchId', params: { batchId: batch.id } });
          }}
        />
      ) : null}
    </PageShell>
  );
}

/** The line above the form after "Record it" (frame i3), or the replay's (design §2.2). */
function OutcomeLine({ outcome }: { outcome: Outcome }) {
  if (outcome.kind === 'already') {
    return (
      <p className={LINE} role="status" data-testid="import-outcome">
        {iw.alreadyRecorded}
      </p>
    );
  }
  return (
    <p className={`${LINE} wrap-anywhere`} role="status" data-testid="import-outcome">
      {iw.recorded(outcome.title, outcome.withFile)}{' '}
      <Link
        to="/records/$recordId"
        params={{ recordId: outcome.recordId }}
        className={`${LINK} text-s13`}
        data-testid="import-outcome-open"
      >
        {iw.openIt}
      </Link>
    </p>
  );
}

/** "Imported so far" (frames i3, i4): the newest entries, each saying how it came in. */
function ImportedSoFar({
  records,
  failed,
  onRetry,
}: {
  records: readonly OrgBoardRecordReadOut[];
  failed: boolean;
  onRetry: () => void;
}) {
  return (
    <Card testId="import-imported-so-far">
      <CardBody className="flex flex-col gap-12">
        <div className="flex flex-col gap-2">
          <h2 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
            {iw.importedHeading}
          </h2>
          <p className={QUIET} data-testid="import-imported-order">
            {iw.importedOrder}
          </p>
        </div>
        {failed ? (
          <div className="flex flex-col items-start gap-12">
            <p className={LINE}>{iw.importedFailed}</p>
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              onClick={onRetry}
              testId="import-imported-retry"
            >
              Try again
            </Button>
          </div>
        ) : (
          <>
            <ul className="flex flex-col">
              {records.map((record) => (
                <li
                  key={record.id}
                  className="flex min-w-0 flex-col gap-2 border-t border-border-subtle py-10 first:border-t-0 first:pt-0"
                  data-testid={`import-imported-${record.id}`}
                >
                  <Link
                    to="/records/$recordId"
                    params={{ recordId: record.id }}
                    className="font-sans text-s14 font-w600 leading-h15 tracking-givmo text-text-strong outline-none transition-colors wrap-anywhere hover:text-brand-700 focus-visible:underline"
                  >
                    {record.title}
                  </Link>
                  <span className={QUIET}>
                    {iw.importedLine(
                      recordKindLabel(record.record_kind),
                      record.occurred_on ? displayDate(record.occurred_on) : null,
                      cameIn(record),
                    )}
                  </span>
                </li>
              ))}
            </ul>
            <Link
              to={BOARD_PAGES.minuteBook}
              className={`${LINK} self-start text-s13`}
              data-testid="import-imported-all"
            >
              {iw.seeThemAll}
            </Link>
          </>
        )}
      </CardBody>
    </Card>
  );
}

/** One of RC8's batches, as built (served only while the batches are). */
function BatchCard({ batch }: { batch: OrgBoardImportBatchOut }) {
  return (
    <Card testId={`import-batch-${batch.id}`}>
      <CardBody className="flex flex-col gap-10">
        <div className="flex flex-wrap items-start justify-between gap-12">
          <div className="flex min-w-0 flex-col gap-4">
            <Link
              to="/import-batches/$batchId"
              params={{ batchId: batch.id }}
              className="font-sans text-s16 font-w600 tracking-givmo text-text-strong outline-none transition-colors hover:text-brand-700 focus-visible:underline"
              data-testid={`import-batch-link-${batch.id}`}
            >
              {batch.label}
            </Link>
            <span className="font-sans text-s13 font-w400 tracking-givmo text-text-secondary">
              {batch.source_description}
            </span>
          </div>
          <div className="flex items-center gap-8">
            <Badge tone="neutral">
              {recordCount(batch)} {recordCount(batch) === 1 ? 'record' : 'records'}
            </Badge>
            <Badge tone={batchStatusTone(batch.status)}>{batchStatusLabel(batch.status)}</Badge>
          </div>
        </div>
        <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
          Started {displayDate(batch.created_at)}
          {batch.closed_at ? ` · closed ${displayInstant(batch.closed_at)}` : ''}
        </span>
      </CardBody>
    </Card>
  );
}

/**
 * Opening a session (served only while the batches are).
 *
 * Both fields are required by the backend, and the description is the load-bearing one: it is the
 * organization's own account of where a corpus came from, and the only place that origin is ever written
 * down. A blank one would leave every record entered under it saying nothing about its provenance.
 */
function OpenBatchDialog({
  orgRef,
  onClose,
  onOpened,
}: {
  orgRef: string;
  onClose: () => void;
  onOpened: (batch: OrgBoardImportBatchOut) => void;
}) {
  const operations = useBoardOperations();
  const [label, setLabel] = useState('');
  const [sourceDescription, setSourceDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  const open = useMutation({
    mutationFn: async (): Promise<OrgBoardImportBatchOut> => {
      const { data } = await operations.officerOpenBoardImportBatch({
        path: { org_ref: orgRef },
        body: { label: label.trim(), source_description: sourceDescription.trim() },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: (batch) => {
      toast.success('Batch started.');
      onOpened(batch);
    },
    onError: (caught) =>
      setError(isGivmoError(caught) ? caught.message : 'We couldn’t start that. Please try again.'),
  });

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !open.isPending && onClose()}>
      <Dialog.Content
        aria-label="Start a batch"
        className="flex flex-col gap-16"
        testId="import-batch-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          Start a batch
        </Dialog.Title>

        <TextField
          label="What to call it"
          value={label}
          maxLength={200}
          onChange={(event) => setLabel(event.target.value)}
          testId="import-batch-label"
        />

        <Textarea
          label="Where these records came from"
          rows={4}
          value={sourceDescription}
          onChange={(event) => setSourceDescription(event.target.value)}
          testId="import-batch-source"
        />
        <FieldNote>
          In your own words — “the secretary’s binder, 2011 to 2019”. Givmo never saw any of this,
          so your account is the only record of where it came from, and it stays with the batch for
          good.
        </FieldNote>

        {error !== null ? <ErrorMessage testId="import-batch-error">{error}</ErrorMessage> : null}

        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={open.isPending}
            onClick={onClose}
            testId="import-batch-cancel"
          >
            Cancel
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={open.isPending}
            disabled={
              label.trim().length === 0 || sourceDescription.trim().length === 0 || open.isPending
            }
            onClick={() => {
              setError(null);
              open.mutate();
            }}
            testId="import-batch-save"
          >
            Start it
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
